import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { OpenInNew, Tag } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { llmsAssets } from "./assets";

const TranslucentWrapper = styled(Flex)`
    padding: 9px;
    border-radius: 9px;
    border: 0.846px solid ${colorsPalettes.carbon[0]};
    background-color: rgba(241, 246, 254, 0.2);
    box-shadow: 0px 0px 4.5px rgba(54, 54, 54, 0.08);
`;

const AdCard = styled(Flex)`
    border-radius: 12px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${colorsPalettes.carbon[0]};
    overflow: hidden;
    filter: drop-shadow(0px 3px 3px rgba(14, 30, 62, 0.08));
`;

const CardTop = styled(Flex)`
    padding: 16px;
    background-color: ${colorsPalettes.carbon[25]};
`;

const CardFooter = styled(Flex)`
    padding: 8px 8px 8px 16px;
    background-color: ${colorsPalettes.carbon[0]};
`;

const FaviconBox = styled(Flex)`
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 3px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${colorsPalettes.carbon[0]};
`;

const FaviconImg = styled.img`
    width: 14px;
    height: 14px;
    object-fit: contain;
`;

const TagBox = styled(Flex)`
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 3px;
    background-color: ${colorsPalettes.carbon[25]};
`;

const FlexFill = styled(Flex)`
    flex: 1;
    min-width: 0;
`;

const TextColumn = styled(Stack)`
    flex: 1;
    min-width: 0;
`;

const WrapText = styled(Text)`
    min-width: 0;
    overflow-wrap: anywhere;
`;

const EllipsisText = styled(Text)`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const FooterActionButton = styled(Flex)`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
`;

const InnerContentCard = styled(Flex)`
    padding: 16px;
    border-radius: 20px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${colorsPalettes.carbon[0]};
`;

const ShoeProductCard = styled(Stack)`
    width: 160px;
    border-radius: 20px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${colorsPalettes.carbon[0]};
    overflow: hidden;
`;

const ShoeImage = styled.img`
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    display: block;
`;

const PeopleImage = styled.img`
    width: 64px;
    height: 64px;
    border-radius: 12px;
    object-fit: cover;
    flex-shrink: 0;
`;

const Avatar = styled.img`
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 50%;
    object-fit: cover;
`;

const FooterAction = () => (
    <FooterActionButton
        alignItems="center"
        justifyContent="center"
    >
        <OpenInNew
            size="xs"
            color={colorsPalettes.blue[400]}
        />
    </FooterActionButton>
);

const FooterTag = ({ label }: { label: string }) => (
    <FlexFill
        gap="8px"
        alignItems="center"
    >
        <TagBox
            alignItems="center"
            justifyContent="center"
        >
            <Tag
                size={14}
                color={colorsPalettes.carbon[400]}
            />
        </TagBox>
        <EllipsisText
            fontVariant="caption"
            color={colorsPalettes.carbon[400]}
        >
            {label}
        </EllipsisText>
    </FlexFill>
);

const PlatformFooter = ({ logo, label }: { logo: string; label: string }) => (
    <FlexFill
        gap="8px"
        alignItems="center"
    >
        <FaviconBox
            alignItems="center"
            justifyContent="center"
        >
            <FaviconImg
                src={logo}
                alt=""
            />
        </FaviconBox>
        <EllipsisText
            fontVariant="caption"
            color={colorsPalettes.carbon[400]}
        >
            {label}
        </EllipsisText>
    </FlexFill>
);

export const HubspotAdCard = () => {
    const { translate } = useTranslate();
    return (
        <TranslucentWrapper flexDirection="column">
            <AdCard
                flexDirection="column"
                width="480px"
                data-automation="llms-channels-ad-card-chatgpt"
            >
                <CardTop
                    flexDirection="column"
                    gap="8px"
                >
                    <Flex
                        gap="8px"
                        alignItems="center"
                    >
                        <Avatar
                            src={llmsAssets.hubspotAvatar}
                            alt=""
                        />
                        <WrapText
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("ad.intelligence.llms.channels.promo.mockup.hubspot.advertiser")}
                        </WrapText>
                    </Flex>
                    <InnerContentCard
                        gap="16px"
                        alignItems="center"
                        width="100%"
                    >
                        <TextColumn gap="4px">
                            <WrapText
                                fontVariant="subtitle2"
                                color={colorsPalettes.carbon[500]}
                            >
                                {translate("ad.intelligence.llms.channels.promo.mockup.hubspot.title")}
                            </WrapText>
                            <WrapText
                                fontVariant="caption"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate("ad.intelligence.llms.channels.promo.mockup.hubspot.description")}
                            </WrapText>
                        </TextColumn>
                        <PeopleImage
                            src={llmsAssets.hubspotPeople}
                            alt=""
                        />
                    </InnerContentCard>
                </CardTop>
                <CardFooter
                    gap="16px"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <PlatformFooter
                        logo={llmsAssets.chatGptLogo}
                        label={translate("ad.intelligence.llms.channels.promo.mockup.hubspot.platform")}
                    />
                    <FooterAction />
                </CardFooter>
            </AdCard>
        </TranslucentWrapper>
    );
};

export const ShoeAdCard = () => {
    const { translate } = useTranslate();
    return (
        <TranslucentWrapper flexDirection="column">
            <AdCard
                flexDirection="column"
                width="420px"
                data-automation="llms-channels-ad-card-google-ai-overview"
            >
                <CardTop
                    flexDirection="column"
                    alignItems="center"
                >
                    <ShoeProductCard>
                        <ShoeImage
                            src={llmsAssets.shoe}
                            alt=""
                        />
                        <Stack
                            gap="4px"
                            padding="8px"
                        >
                            <WrapText
                                fontVariant="subtitle2"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate("ad.intelligence.llms.channels.promo.mockup.shoe.title")}
                            </WrapText>
                            <WrapText
                                fontVariant="subtitle2"
                                color={colorsPalettes.carbon[400]}
                            >
                                {translate("ad.intelligence.llms.channels.promo.mockup.shoe.price")}
                            </WrapText>
                            <WrapText
                                fontVariant="caption"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate("ad.intelligence.llms.channels.promo.mockup.shoe.brand")}
                            </WrapText>
                        </Stack>
                    </ShoeProductCard>
                </CardTop>
                <CardFooter
                    gap="16px"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <FlexFill
                        gap="8px"
                        alignItems="center"
                    >
                        <PlatformFooter
                            logo={llmsAssets.googleAiOverviewLogo}
                            label={translate("ad.intelligence.llms.channels.promo.mockup.shoe.platform")}
                        />
                        <FooterTag label={translate("ad.intelligence.llms.channels.promo.mockup.shoe.tag")} />
                    </FlexFill>
                    <FooterAction />
                </CardFooter>
            </AdCard>
        </TranslucentWrapper>
    );
};
